/**
 * Logo Upload Zone Component
 * Drag-and-drop file upload for media logos with preview
 */

import { useCallback } from 'react';
import { Upload, X, Crop } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Card } from '@/core/components/ui/card';
import { cn } from '@/core/lib/utils';
import { AssetReference } from '../../types/asset-types';
import { PendingLogoUpload } from '../../../kiosk/types/kiosk-types';
import { ALLOWED_LOGO_EXTENSIONS, formatFileSize } from '../../utils/file-helpers';

interface LogoUploadZoneProps {
  label: string;
  target: 'media4' | 'media5';
  currentLogo?: AssetReference;
  pendingLogo?: PendingLogoUpload;
  disabled?: boolean;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  onCrop?: () => void;
}

export function LogoUploadZone({
  label,
  target,
  currentLogo,
  pendingLogo,
  disabled = false,
  onFileSelect,
  onRemove,
  onCrop,
}: LogoUploadZoneProps) {
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onFileSelect(files[0]);
      }
    },
    [disabled, onFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        onFileSelect(files[0]);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [onFileSelect]
  );

  const hasLogo = pendingLogo || currentLogo;
  const displayUrl = pendingLogo?.previewUrl || currentLogo?.s3Url;

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{label}</p>
        {pendingLogo && (
          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
            Pending Upload
          </span>
        )}
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 transition-colors',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary',
          hasLogo ? 'border-gray-200' : 'border-gray-300'
        )}
      >
        {/* File Input - disabled pointer events when logo exists to prevent conflicts */}
        <input
          type="file"
          id={`logo-upload-${target}`}
          accept={ALLOWED_LOGO_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          disabled={disabled}
          className={cn(
            'absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed',
            hasLogo && 'pointer-events-none'
          )}
        />

        {hasLogo && displayUrl ? (
          /* Preview Mode */
          <div className="space-y-3">
            <div className="relative w-full h-32 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
              <img
                src={displayUrl}
                alt={`${target} logo preview`}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {pendingLogo && (
              <div className="text-xs text-gray-600">
                <p>File: {pendingLogo.file.name}</p>
                <p>Size: {formatFileSize(pendingLogo.file.size)}</p>
              </div>
            )}

            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {pendingLogo && onCrop && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCrop();
                  }}
                  disabled={disabled}
                  className="flex-1"
                >
                  <Crop className="h-4 w-4 mr-2" />
                  Crop
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove();
                }}
                disabled={disabled}
                className={cn(pendingLogo && onCrop ? 'flex-1' : 'w-full')}
              >
                <X className="h-4 w-4 mr-2" />
                Remove
              </Button>
            </div>
          </div>
        ) : (
          /* Upload Mode */
          <div className="text-center space-y-2">
            <Upload className="h-8 w-8 mx-auto text-gray-400" />
            <div className="text-sm">
              <p className="font-medium text-gray-700">Drop logo here or click to upload</p>
              <p className="text-gray-500 text-xs mt-1">
                {ALLOWED_LOGO_EXTENSIONS.join(', ').toUpperCase()} (max 2MB)
              </p>
            </div>
          </div>
        )}
      </div>

      {!hasLogo && (
        <p className="text-xs text-gray-500">
          Upload a logo for {target === 'media4' ? '4-inch' : '5-inch'} kiosk displays. You can
          crop the image after uploading.
        </p>
      )}
    </Card>
  );
}
